import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Route as salesOneRoute } from "@/routes/(pharma)/_layout/overview/sales_history/$saleId";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { PaymentMode, SaleStatus } from "@/gen/sales/common_pb";
dayjs.extend(customParseFormat);

const CompanyDetails: React.FC = () => {
  const data = salesOneRoute.useLoaderData();
  const navigate = useNavigate();
  const searchParams = salesOneRoute.useSearch();

  return (
    <div className="col-span-1 lg:max-w-[400px] overflow-hidden border-r-[0.5px] h-full border-gray-200 shadow-transparent">
      <CardHeader className="p-0 space-y-0 flex flex-row items-start ">
        <div className="grid gap-0.5">
          <CardTitle className="group flex items-center font-bold text-2xl">
            {data.receiptNumber}
          </CardTitle>
          <CardDescription className="text-gray-500 text-sm">
            Status: {Object.keys(SaleStatus).find(
              key => SaleStatus[key as keyof typeof SaleStatus] === data.saleValues?.status
            ) || "Unknown"}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="p-4 pl-0 text-xs">
        <div className="grid gap-2">
          {data.isValidPatient && (
            <p className="text-base font-bold">Customer Details</p>
          )}
          {data.isValidPatient && (
            <div className="grid grid-cols-4 gap-y-6 my-2">
              <p className=" text-gray-500 whitespace-nowrap col-span-2">
                Customer Type
              </p>
              <p className=" text-gray-500 font-medium col-span-2">
                Registered
              </p>
              <p className="text-gray-500 whitespace-nowrap col-span-2">
                Full Name
              </p>
              <p className=" text-gray-500 font-medium col-span-2">
                {data.patient?.name}
              </p>

              <p className="text-gray-500 whitespace-nowrap col-span-2">
                Government Identifier
              </p>
              <p className=" text-gray-500 font-medium col-span-2">
                {data.patient?.govtIdentifier || "Not Available"}
              </p>

              <p className="text-gray-500 whitespace-nowrap col-span-2">
                Delivery Details
              </p>
              <p className=" text-gray-500 font-medium col-span-2">
                {data.patient?.metadata?.address}
              </p>

              <p className="text-gray-500 whitespace-nowrap col-span-2">
                Contact details
              </p>
              <div className="flex flex-col col-span-2">
                <p className=" text-gray-500 font-medium">
                  {data.patient?.phone}
                </p>
              </div>
            </div>
          )}
          {!data.isValidPatient && (
            <div className="border-b border-gray-300">
              <p className="text-base font-bold">Customer Details</p>
            </div>
          )}
          {!data.isValidPatient && (
            <div className="grid grid-cols-2 gap-y-4">
              <p className="text-gray-500 whitespace-nowrap">Customer Type</p>
              <p className=" text-gray-700">Unregistered</p>
              <p className="text-gray-500 whitespace-nowrap">Customer Name</p>
              <p className=" text-gray-700">Over the Counter Customer</p>
            </div>
          )}
          <div className="flex justify-between items-center border-b border-gray-300 mb-2">
            <p className="text-base font-bold mb-4">Transaction Details</p>
          </div>
          <ul className="flex flex-wrap gap-10 gap-y-4">
            <li className="flex flex-col justify-between mb-2">
              <span className="text-gray-500">Paid Amount</span>
              <span className=" text-gray-500 font-bold">
                {convertToCurrencyRounded(
                  data.saleValues?.grandTotalAmount!
                )}
              </span>
            </li>
            <li className="flex flex-col justify-between mb-2">
              <span className="text-gray-500">Total Amount</span>
              <span className=" text-gray-500 font-bold">
                {convertToCurrencyRounded(
                  data.saleValues?.netAmountBeforeTax!
                )}
              </span>
            </li>
            <li className="flex flex-col justify-between mb-2">
              <span className="text-gray-500">Discount</span>
              <span className=" text-gray-500 font-bold">
                {convertToCurrencyRounded(data.saleValues?.totalItemDiscountAmount!)}
              </span>
            </li>
            {/* <li className="flex flex-col justify-between mb-2 mr-4">
              <span className="text-gray-500">Refunded</span>
              <span className=" text-gray-500 font-bold">
                {data.sale_status.refunded ? "Yes" : "No"}
              </span>
            </li>
            <li className="flex flex-col justify-between mb-2">
              <span className="text-gray-500">Void</span>
              <span className=" text-gray-500 font-bold">
                {data.sale_status.void ? "Yes" : "No"}
              </span>
            </li> */}
          </ul>
          <div className="grid grid-cols-2 gap-y-4">
            <p className="text-gray-500 whitespace-nowrap">Tax</p>
            <p className=" text-gray-500 font-bold">
              {convertToCurrencyRounded(data.saleValues?.totalTaxAmount!)}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-y-4">
            <p className="text-gray-500 whitespace-nowrap">Sale Time</p>
            <p className=" text-gray-700 font-semibold">
              {dayjs(data.saleValues?.createdAt?.toDate()).format("DD MMM, YYYY hh:mm A")}
            </p>
          </div>
          <div className="flex justify-between items-center border-b border-gray-300 mb-2">
            <p className="text-base font-bold mb-4">Payment Details</p>
          </div>
          <ul className="flex flex-wrap gap-10 gap-y-4">
            {data.payments.map(payment => <li className="flex flex-col justify-between mb-2" key={payment.paymentMode}>
              <span className="text-gray-500">{
                  Object.keys(PaymentMode)
                    .find(key => PaymentMode[key as keyof typeof PaymentMode] === payment.paymentMode)
                    ?.replace(/_/g, " ") || "Unknown Payment Mode"
                }</span>
              <span className=" text-gray-500 font-bold">
                {convertToCurrencyRounded(
                  payment.amount
                )}
              </span>
            </li>)}
          </ul>
        </div>
      </CardContent>
    </div>
  );
};

export default CompanyDetails;
